import PageLayout from "@/Themes/Default/Layouts/PageLayout";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import React, {useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {Button} from "primereact/button";
import {useDebounce} from "react-use";
import BaseTextInput from "@/Components/Forms/Form/BaseTextInput";
import {toNormalCase, url} from "@/Helpers/general";
import {useNavigate} from "react-router-dom";
import {useUpdateSearchParams} from "@/Hooks/useUpdateSearchParams";
import useGetGtaRatesFiltersQuery, {GtaRatesFilterType} from "@/Hooks/useGetGtaRatesFiltersQuery";
import GtaRatesDatatable from "@/Components/Datatables/GtaRatesDatatable";
import {useGetGtaRateConfig} from "@/Service/Api/V1/ApiHooks/GtaRates/useGetGtaRateConfig";
import {useToastMessagesStore} from "@/Stores/ToastMessagesStore";
import {CustomErrorMessage} from "@/Messages/Toast/General/CustomErrorMessage";
import {downloadBlob} from "@/Util/downloadBlob";
import UploadGtaRatesConfigModal from "@/Components/Modals/UploadGtaRatesConfigModal";
import BaseDropdown from "@/Components/Forms/Form/BaseDropdown";
import {GetGtaRateListRequest, GtaRateStatusEnum} from "@/Stub/V1";

const StyledWrap = styled.main`
    .content-container {
        margin-top: 2rem;
        padding: 0 1rem;
    }

    .page-actions {
        margin-top: 2rem;
        padding: 1rem;
        background: #fff;
        border-radius: 8px;
    }
`;

const GtaRatesPage: React.FC = () => {
    const navigate = useNavigate();
    const updateGtaRatesFilterHandler = useUpdateSearchParams();
    const addToastMessage = useToastMessagesStore((state) => state.addToastMessage);
    const {
        pageQuery,
        searchQuery,
        pageSizeQuery,
        statusQuery
    } = useGetGtaRatesFiltersQuery();

    const [inputValue, setInputValue] = useState(searchQuery || '');
    const [uploadModalVisible, setUploadModalVisible] = useState(false);

    const {
        refetch: getConfig,
        isRefetching: isGetConfigPending
    } = useGetGtaRateConfig({enabled: false});

    useDebounce(
        () => {
            updateGtaRatesFilterHandler(GtaRatesFilterType.Search, inputValue);
        },
        1000,
        [inputValue]
    );

    const requestParams: Partial<GetGtaRateListRequest> = useMemo(() => {
        return {
            page: pageQuery,
            per_page: pageSizeQuery,
            search: searchQuery || undefined,
            status: statusQuery === 'all' ? undefined : statusQuery
        };
    }, [searchQuery, pageQuery, pageSizeQuery, statusQuery]);

    const statusOptions = useMemo(() => {
        return [
            {key: 'all', label: 'All', value: 'all'},
            ...Object.entries(GtaRateStatusEnum).map(([key, value]) => ({
                key,
                value,
                label: toNormalCase(value),
            }))
        ];
    }, []);

    const handleDownloadConfig = async () => {
        await getConfig().then((config) => {
            if (config.error) {
                addToastMessage(CustomErrorMessage(config.error));
            } else {
                downloadBlob(config.data as Blob);
            }
        });

    };

    useEffect(() => {
        updateGtaRatesFilterHandler(GtaRatesFilterType.Status, GtaRateStatusEnum.Active);
    }, []);

    return (
        <PageLayout>
            <StyledWrap>
                <div className="flex justify-content-between content-header">
                    <div>
                        <h2 className="content-header__title">GTA Rates</h2>
                    </div>
                    <div className={'flex gap-2'}>
                        <Button
                            loading={isGetConfigPending}
                            onClick={() => navigate(url('create-gta-rate'))}
                            label={'Create GTA Rate'}
                        />
                        <Button
                            loading={isGetConfigPending}
                            onClick={() => handleDownloadConfig()}
                            label={'Download Config'}
                        />
                        <Button
                            loading={isGetConfigPending}
                            onClick={() => setUploadModalVisible(true)}
                            label={'Upload Config'}
                        />
                    </div>
                </div>
                <div className="flex align-items-end gap-3">
                    <div className={'flex align-items-center relative'}>
                        <BaseTextInput
                            value={inputValue || ''}
                            placeholder="Search"
                            onChange={(e) => {
                                setInputValue(e.target.value);
                            }}
                        />
                        {inputValue && <span
                            className="pi pi-times absolute right-0 pr-2 cursor-pointer"
                            onClick={() => {
                                setInputValue('')
                            }}
                        />}
                    </div>
                    <BaseDropdown
                        className={'w-10rem'}
                        pt={{dropdown: {className: 'w-full'}}}
                        name='status'
                        value={statusQuery || 'all'}
                        label="Status"
                        onChange={(e) => {
                            updateGtaRatesFilterHandler(GtaRatesFilterType.Status, e.value);
                        }}
                        options={statusOptions}
                    />
                </div>
            </StyledWrap>

            <div className="content-container">
                <GtaRatesDatatable requestParams={requestParams}/>
            </div>
            <UploadGtaRatesConfigModal
                dialogProps={{
                    visible: uploadModalVisible,
                    onHide: () => setUploadModalVisible(false)
                }}
            />
        </PageLayout>
    );
};

export default withAuthenticationRequired(GtaRatesPage);
